import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'config/hooks';
import {
    doArchiveInvoice,
    doDuplicateInvoice,
    doGetInvoiceById,
    doListInvoices,
    doSendInvoiceToCustomer,
    doUnarchiveInvoice,
    doUpdateInvoice,
    doUpdateInvoiceStatus,
} from 'store/invoices/actions';
import {
    ListInvoicesParams,
    SendInvoiceToCustomerPayload,
    UpdateInvoicePayload,
    UpdateInvoiceStatusPayload,
} from 'store/invoices/types';

const useInvoices = (invoiceId?: string) => {
    const dispatch = useAppDispatch();

    const selectedInvoiceId = useAppSelector((state) => state.invoices.currentInvoiceId);
    const invoice = useAppSelector((state) => (invoiceId ? state.invoices.entities[invoiceId] : undefined));
    const isLoading = useAppSelector((state) => state.invoices.isLoading);
    const error = useAppSelector((state) => state.invoices.error);
    const invoicesListResponse = useAppSelector((s) => s.invoices.invoicesListResponse);
    const invoices = invoicesListResponse.list;

    const getInvoiceById = useCallback((invoiceId: string) => dispatch(doGetInvoiceById(invoiceId)), [dispatch]);
    const updateInvoice = useCallback(
        (invoiceId: string, payload: UpdateInvoicePayload) => dispatch(doUpdateInvoice(invoiceId, payload)),
        [dispatch]
    );
    const listInvoices = useCallback((params?: ListInvoicesParams) => dispatch(doListInvoices(params)), [dispatch]);
    const archiveInvoice = useCallback((invoiceId: string) => dispatch(doArchiveInvoice(invoiceId)), [dispatch]);
    const unarchiveInvoice = useCallback((invoiceId: string) => dispatch(doUnarchiveInvoice(invoiceId)), [dispatch]);
    const duplicateInvoice = useCallback((invoiceId: string) => dispatch(doDuplicateInvoice(invoiceId)), [dispatch]);
    const updateInvoiceStatus = useCallback(
        (invoiceId: string, payload: UpdateInvoiceStatusPayload) => dispatch(doUpdateInvoiceStatus(invoiceId, payload)),
        [dispatch]
    );

    const sendInvoiceToCustomer = useCallback(
        (invoiceId: string, payload: SendInvoiceToCustomerPayload) =>
            dispatch(doSendInvoiceToCustomer(invoiceId, payload)),
        [dispatch]
    );

    useEffect(() => {
        if (invoiceId && invoiceId !== invoice?.id) {
            getInvoiceById(invoiceId);
        }
    }, [getInvoiceById, invoice?.id, invoiceId]);

    return {
        selectedInvoiceId,
        invoice,
        invoices,
        isLoading,
        error,
        invoicesListResponse,
        getInvoiceById,
        updateInvoice,
        listInvoices,
        archiveInvoice,
        unarchiveInvoice,
        duplicateInvoice,
        sendInvoiceToCustomer,
        updateInvoiceStatus,
    };
};

export default useInvoices;
